import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { loadFailureKind, pilotErrorMessage } from "../lib/pilots";

// Loads GET /prospects/{id}/pilot and keeps it. Shared by the prospect page's Pilote tab and the standalone workspace.
// status: loading | ready | none (no pilot yet) | notfound | forbidden | error.
// A response that arrives after a newer request, a mutation result or a change of prospect is dropped (`seq`).
export default function usePilot(prospectId) {
  const [pilotState, setPilotState] = useState({ status: "loading" });
  const seq = useRef(0);

  const loadPilot = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setPilotState({ status: "loading" });
    try {
      const { data } = await api.get(`/prospects/${prospectId}/pilot`);
      if (mine === seq.current) setPilotState({ status: "ready", data });
    } catch (e) {
      if (mine !== seq.current) return;
      // 404 "Pilote non créé" is the normal "no pilot yet" answer; any other 404 means the prospect is not reachable.
      if (e.response?.status === 404 && e.response?.data?.detail === "Pilote non créé") { setPilotState({ status: "none" }); return; }
      const kind = loadFailureKind(e);
      const failed = kind === "error" ? { status: "error", error: pilotErrorMessage(e, "Impossible de charger le pilote.") } : { status: kind };
      // a failed background refresh keeps what is displayed
      setPilotState((s) => (silent && s.status === "ready" ? s : failed));
    }
  }, [prospectId]);

  useEffect(() => {
    loadPilot();
    return () => { seq.current += 1; };
  }, [loadPilot]);

  // The result of a mutation is newer than any request still in flight.
  const setPilotData = useCallback((data) => {
    seq.current += 1;
    setPilotState({ status: "ready", data });
  }, []);

  return { pilotState, loadPilot, setPilotData };
}
